import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiUrl } from "../config/api";
import "./css/profile.css";

const emptyProfile = {
  username: "",
  name: "",
  birthDate: "",
  rut: "",
  bank: "",
  bankAccount: "",
  bankAccountType: "",
  role: "",
  googleEmail: "",
  personalEmail: "",
  createdIn: "",
  modules: [],
  locations: [],
};

function getSessionHeaders() {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    "Content-Type": "application/json",
  };
}

async function readJsonResponse(response, fallbackMessage) {
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error(fallbackMessage);
  }
  return response.json();
}

function normalizeProfile(profile) {
  return {
    ...emptyProfile,
    ...profile,
    bank: profile?.bank ? String(profile.bank) : "",
    bankAccountType: profile?.bankAccountType ? String(profile.bankAccountType) : "",
  };
}

function getRoleLabel(role) {
  if (!role) return "Sin rol";
  return String(role)
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");
}

function formatDateLabel(value) {
  if (!value) return "Sin fecha";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function Profile() {
  const [profile, setProfile] = useState(emptyProfile);
  const [draft, setDraft] = useState(emptyProfile);
  const [banks, setBanks] = useState([]);
  const [bankAccountTypes, setBankAccountTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const hasChanges = useMemo(() => (
    JSON.stringify(profile) !== JSON.stringify(draft)
  ), [draft, profile]);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setMessage("");
    try {
      const response = await fetch(apiUrl("/user/profile"), {
        headers: getSessionHeaders(),
      });
      const data = await readJsonResponse(
        response,
        "La API de perfil no esta disponible. Reinicia el API para cargar los endpoints nuevos."
      );

      if (!response.ok || !data.success) {
        throw new Error(data.message || "No se pudo cargar el perfil.");
      }

      const normalized = normalizeProfile(data.profile || {});
      setProfile(normalized);
      setDraft(normalized);
      setBanks(data.options?.banks || []);
      setBankAccountTypes(data.options?.bankAccountTypes || []);
    } catch (err) {
      setMessage(err.message || "No se pudo cargar el perfil.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  function updateDraft(field, value) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  async function handleSave(event) {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const response = await fetch(apiUrl("/user/profile"), {
        method: "PATCH",
        headers: getSessionHeaders(),
        body: JSON.stringify({
          name: draft.name,
          personalEmail: draft.personalEmail,
          birthDate: draft.birthDate,
          rut: draft.rut,
          bank: draft.bank || null,
          bankAccount: draft.bankAccount,
          bankAccountType: draft.bankAccountType || null,
        }),
      });
      const data = await readJsonResponse(response, "No se pudo guardar el perfil.");

      if (!response.ok || !data.success) {
        throw new Error(data.message || "No se pudo guardar el perfil.");
      }

      const normalized = normalizeProfile(data.profile || {});
      setProfile(normalized);
      setDraft(normalized);
      setMessage("Perfil actualizado.");
    } catch (err) {
      setMessage(err.message || "No se pudo guardar el perfil.");
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    setDraft(profile);
    setMessage("");
  }

  return (
    <main className="profile-view">
      <header className="profile-view__header">
        <div>
          <p>MI CUENTA</p>
          <h1>Perfil</h1>
          <span>Actualiza tus datos personales y de pago asociados a tu usuario.</span>
        </div>
      </header>

      {loading ? (
        <section className="profile-view__panel profile-view__empty">
          <span>Cargando perfil...</span>
        </section>
      ) : (
        <form className="profile-view__content" onSubmit={handleSave}>
          <section className="profile-view__summary" aria-label="Resumen de cuenta">
            <article>
              <span>Usuario</span>
              <strong>{profile.username || "Sin usuario"}</strong>
            </article>
            <article>
              <span>Rol</span>
              <strong>{getRoleLabel(profile.role)}</strong>
            </article>
            <article>
              <span>Correo OVM</span>
              <strong>{profile.googleEmail || "Sin correo"}</strong>
            </article>
            <article>
              <span>Fecha de nacimiento</span>
              <strong>{formatDateLabel(profile.birthDate)}</strong>
            </article>
          </section>

          <section className="profile-view__panel">
            <div className="profile-view__section-heading">
              <p>DATOS PERSONALES</p>
              <h2>Informacion principal</h2>
            </div>

            <div className="profile-view__grid">
              <label>
                <span>Nombre</span>
                <input
                  value={draft.name}
                  onChange={(event) => updateDraft("name", event.target.value)}
                  placeholder="Nombre completo"
                  required
                />
              </label>
              <label>
                <span>Correo personal</span>
                <input
                  type="email"
                  value={draft.personalEmail}
                  onChange={(event) => updateDraft("personalEmail", event.target.value)}
                  placeholder="correo@dominio.cl"
                />
              </label>
              <label>
                <span>RUT</span>
                <input
                  value={draft.rut}
                  onChange={(event) => updateDraft("rut", event.target.value)}
                  placeholder="12.345.678-9"
                />
              </label>
              <label>
                <span>Fecha de nacimiento</span>
                <input
                  type="date"
                  value={draft.birthDate}
                  onChange={(event) => updateDraft("birthDate", event.target.value)}
                />
              </label>
            </div>
          </section>

          <section className="profile-view__panel">
            <div className="profile-view__section-heading">
              <p>DATOS DE PAGO</p>
              <h2>Cuenta bancaria</h2>
            </div>

            <div className="profile-view__grid">
              <label>
                <span>Banco</span>
                <select
                  value={draft.bank}
                  onChange={(event) => updateDraft("bank", event.target.value)}
                >
                  <option value="">Selecciona banco</option>
                  {banks.map((bank) => (
                    <option key={bank.id} value={bank.id}>{bank.name}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Tipo de cuenta</span>
                <select
                  value={draft.bankAccountType}
                  onChange={(event) => updateDraft("bankAccountType", event.target.value)}
                >
                  <option value="">Selecciona tipo</option>
                  {bankAccountTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </label>
              <label className="profile-view__wide-field">
                <span>Numero de cuenta</span>
                <input
                  value={draft.bankAccount}
                  onChange={(event) => updateDraft("bankAccount", event.target.value)}
                  placeholder="Numero de cuenta"
                />
              </label>
            </div>
          </section>

          <section className="profile-view__panel profile-view__access">
            <div className="profile-view__section-heading">
              <p>ACCESOS</p>
              <h2>Permisos asignados</h2>
            </div>
            <div className="profile-view__readonly-grid">
              <div>
                <span>Modulos</span>
                <strong>{profile.modules?.length ? profile.modules.join(", ") : "Sin modulos"}</strong>
              </div>
              <div>
                <span>Ubicaciones</span>
                <strong>{profile.locations?.length ? profile.locations.join(", ") : "Sin ubicaciones"}</strong>
              </div>
              <div>
                <span>Origen</span>
                <strong>{profile.createdIn || "Sin origen"}</strong>
              </div>
            </div>
          </section>

          <footer className="profile-view__footer">
            <span className={message.includes("actualizado") ? "is-success" : ""}>{message}</span>
            <div>
              <button
                type="button"
                className="profile-view__button profile-view__button--secondary"
                onClick={handleReset}
                disabled={!hasChanges || saving}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="profile-view__button profile-view__button--primary"
                disabled={!hasChanges || saving}
              >
                {saving ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </footer>
        </form>
      )}
    </main>
  );
}

export default Profile;
